import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Badge,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Checkbox,
  Tooltip,
  Input,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Combobox,
  Option,
  Spinner,
  Link as FluentLink,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  DocumentRegular,
  ArrowDownloadRegular,
  OpenRegular,
  FilterRegular,
  SearchRegular,
  LinkRegular,
  FolderZipRegular,
} from '@fluentui/react-icons';
import { getPortalUploads, reassignPortalUpload, downloadPortalUploadsZip, getTaxReturns, type PortalUpload } from '../services/api';
import { realLinkProps } from '../components/links/realLinkProps';
import { TableSkeleton } from '../components/LoadingSkeleton';

const useStyles = makeStyles({
  filterBar: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  searchInput: {
    minWidth: '200px',
  },
  clientLink: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
});

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function ClientUploadsPage() {
  const { t } = useTranslation();
  const styles = useStyles();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [generalOnly, setGeneralOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [assignTarget, setAssignTarget] = useState<PortalUpload | null>(null);
  const [bulkAssignDocs, setBulkAssignDocs] = useState<PortalUpload[] | null>(null);
  const [assignSelectedId, setAssignSelectedId] = useState<string>('');
  const [assignFilter, setAssignFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [lastClickedId, setLastClickedId] = useState<{ id: string; group: 'general' | 'linked' } | null>(null);
  const [zipping, setZipping] = useState(false);
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const [bulkAssignProgress, setBulkAssignProgress] = useState<{ current: number; total: number } | null>(null);
  const [bulkAssignError, setBulkAssignError] = useState<string | null>(null);
  // Undo state — tracks the last assign (single or bulk) so it can be reversed
  const [lastAssignOp, setLastAssignOp] = useState<{
    movedDocs: Array<{ id: string; fromReturnId: string; toReturnId: string; fileName: string }>;
    targetName: string;
  } | null>(null);
  const [undoing, setUndoing] = useState(false);
  // Return-Linked table column filters
  const [linkedClientFilter, setLinkedClientFilter] = useState('');
  const [linkedYearFilter, setLinkedYearFilter] = useState('');
  const [linkedDateRangeFilter, setLinkedDateRangeFilter] = useState<'all' | '7d' | '30d' | '90d' | 'thisYear'>('all');
  const [linkedFileTypeFilter, setLinkedFileTypeFilter] = useState('');

  const { data: documents = [], isLoading, error, refetch } = useQuery<PortalUpload[]>({
    queryKey: ['portalUploads', generalOnly],
    queryFn: () => getPortalUploads(generalOnly),
  });

  // Spec §4.3 (D2c, 2026-08-18 portal-upload-routing spec): the one-click reassign button needs the
  // target return's client name + year up front, not just after a dialog opens — so this also loads
  // whenever any fetched doc carries a sourceReturnId, not only when assignTarget/bulkAssignDocs is set.
  const { data: allReturns = [] } = useQuery({
    queryKey: ['taxReturnsForAssignment'],
    queryFn: () => getTaxReturns(),
    enabled: assignTarget !== null || bulkAssignDocs !== null || documents.some(d => Boolean(d.sourceReturnId)),
  });

  const returnsById = useMemo(() => new Map(allReturns.map(r => [r.id, r])), [allReturns]);

  const reassignMutation = useMutation({
    mutationFn: ({ doc, newReturnId }: { doc: PortalUpload; newReturnId: string }) =>
      reassignPortalUpload(doc.id, doc.taxReturnId, newReturnId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['portalUploads'] });
      // Record for undo
      const picked = allReturns.find(r => r.id === variables.newReturnId);
      const targetName = picked ? `${picked.clientName} (${picked.taxYear})` : t('clientUploadsPage.undoBanner.fallbackTargetName');
      setLastAssignOp({
        movedDocs: [{
          id: variables.doc.id,
          fromReturnId: variables.doc.taxReturnId,
          toReturnId: variables.newReturnId,
          fileName: variables.doc.fileName,
        }],
        targetName,
      });
      setAssignTarget(null);
      setAssignSelectedId('');
      setAssignFilter('');
    },
  });

  // Auto-hide the undo banner after 20 seconds
  useEffect(() => {
    if (!lastAssignOp) return;
    const timer = window.setTimeout(() => setLastAssignOp(null), 20000);
    return () => window.clearTimeout(timer);
  }, [lastAssignOp]);

  const filteredDocuments = useMemo(() => {
    if (!searchQuery.trim()) return documents;
    const query = searchQuery.toLowerCase();
    return documents.filter(doc =>
      doc.fileName.toLowerCase().includes(query) ||
      (doc.uploadedByName && doc.uploadedByName.toLowerCase().includes(query)) ||
      (doc.emailFrom && doc.emailFrom.toLowerCase().includes(query)) ||
      (doc.clientName && doc.clientName.toLowerCase().includes(query))
    );
  }, [documents, searchQuery]);

  const generalDocs = filteredDocuments.filter(d => d.taxReturnId === 'general');
  const linkedDocs = filteredDocuments.filter(d => d.taxReturnId !== 'general');

  // Filter option lists, derived from the un-filtered linkedDocs so they always
  // reflect what's actually present (and so picking one filter doesn't empty out
  // the options of the others).
  const linkedClientOptions = useMemo(() => {
    const set = new Set<string>();
    for (const d of linkedDocs) {
      if (d.clientName) set.add(d.clientName);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [linkedDocs]);

  const linkedYearOptions = useMemo(() => {
    const set = new Set<number>();
    for (const d of linkedDocs) {
      if (d.taxYear) set.add(d.taxYear);
    }
    return Array.from(set).sort((a, b) => b - a);
  }, [linkedDocs]);

  const linkedFileTypeOptions = useMemo(() => {
    const set = new Set<string>();
    for (const d of linkedDocs) {
      if (d.fileType) set.add(d.fileType.toUpperCase());
    }
    return Array.from(set).sort();
  }, [linkedDocs]);

  const linkedDocsFiltered = useMemo(() => linkedDocs.filter(d => {
    if (linkedClientFilter && d.clientName !== linkedClientFilter) return false;
    if (linkedYearFilter && String(d.taxYear ?? '') !== linkedYearFilter) return false;
    if (linkedFileTypeFilter && (d.fileType?.toUpperCase() ?? '') !== linkedFileTypeFilter) return false;
    if (linkedDateRangeFilter !== 'all') {
      const uploaded = new Date(d.uploadedAt);
      if (Number.isNaN(uploaded.getTime())) return true;
      if (linkedDateRangeFilter === 'thisYear') {
        if (uploaded.getFullYear() !== new Date().getFullYear()) return false;
      } else {
        const days = linkedDateRangeFilter === '7d' ? 7 : linkedDateRangeFilter === '30d' ? 30 : 90;
        if (Date.now() - uploaded.getTime() > days * 86400 * 1000) return false;
      }
    }
    return true;
  }), [linkedDocs, linkedClientFilter, linkedYearFilter, linkedFileTypeFilter, linkedDateRangeFilter]);

  const linkedFiltersActive =
    linkedClientFilter !== '' ||
    linkedYearFilter !== '' ||
    linkedFileTypeFilter !== '' ||
    linkedDateRangeFilter !== 'all';

  const clearLinkedFilters = () => {
    setLinkedClientFilter('');
    setLinkedYearFilter('');
    setLinkedFileTypeFilter('');
    setLinkedDateRangeFilter('all');
  };

  const dateRangeLabel = (v: typeof linkedDateRangeFilter) =>
    v === 'all' ? t('clientUploadsPage.linked.dateRange.all')
    : v === '7d' ? t('clientUploadsPage.linked.dateRange.last7')
    : v === '30d' ? t('clientUploadsPage.linked.dateRange.last30')
    : v === '90d' ? t('clientUploadsPage.linked.dateRange.last90')
    : t('clientUploadsPage.linked.dateRange.thisYear');

  const toggleSelected = (
    id: string,
    group: 'general' | 'linked',
    groupDocs: PortalUpload[],
    shiftKey: boolean,
  ) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      // Shift-click: select the range between last clicked (same group) and current
      if (shiftKey && lastClickedId && lastClickedId.group === group) {
        const startIdx = groupDocs.findIndex(d => d.id === lastClickedId.id);
        const endIdx = groupDocs.findIndex(d => d.id === id);
        if (startIdx !== -1 && endIdx !== -1) {
          const [lo, hi] = startIdx < endIdx ? [startIdx, endIdx] : [endIdx, startIdx];
          // Use the target row's new state to decide whether to select or deselect the range
          const shouldSelect = !next.has(id);
          for (let i = lo; i <= hi; i++) {
            if (shouldSelect) next.add(groupDocs[i].id);
            else next.delete(groupDocs[i].id);
          }
          return next;
        }
      }
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setLastClickedId({ id, group });
  };

  const toggleSelectAll = (docs: PortalUpload[]) => {
    const ids = docs.map(d => d.id);
    const allSelected = ids.every(id => selectedIds.has(id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allSelected) ids.forEach(id => next.delete(id));
      else ids.forEach(id => next.add(id));
      return next;
    });
  };

  const handleDownloadZip = async () => {
    const selected = filteredDocuments.filter(d => selectedIds.has(d.id));
    if (selected.length === 0) return;
    setZipping(true);
    try {
      const zipBlob = await downloadPortalUploadsZip(
        selected.map(d => ({ id: d.id, taxReturnId: d.taxReturnId })),
      );
      if (zipBlob.size === 0) {
        throw new Error(t('clientUploadsPage.errors.emptyZipError'));
      }
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `client-uploads-${stamp}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Revoke on next tick so the browser has a chance to start the download
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error('Failed to download zip', err);
      alert(t('clientUploadsPage.errors.downloadZipFailed', {
        message: err instanceof Error ? err.message : t('clientUploadsPage.errors.unknownError'),
      }));
    } finally {
      setZipping(false);
    }
  };

  const selectedGeneralDocs = useMemo(
    () => filteredDocuments.filter(d => selectedIds.has(d.id) && d.taxReturnId === 'general'),
    [filteredDocuments, selectedIds],
  );
  const selectedLinkedDocs = useMemo(
    () => filteredDocuments.filter(d => selectedIds.has(d.id) && d.taxReturnId !== 'general'),
    [filteredDocuments, selectedIds],
  );

  const handleBulkAssign = async () => {
    if (!assignSelectedId || !bulkAssignDocs || bulkAssignDocs.length === 0) return;
    setBulkAssigning(true);
    setBulkAssignError(null);
    setBulkAssignProgress({ current: 0, total: bulkAssignDocs.length });
    const failures: string[] = [];
    const succeeded: Array<{ id: string; fromReturnId: string; toReturnId: string; fileName: string }> = [];
    for (let i = 0; i < bulkAssignDocs.length; i++) {
      const doc = bulkAssignDocs[i];
      try {
        await reassignPortalUpload(doc.id, doc.taxReturnId, assignSelectedId);
        succeeded.push({
          id: doc.id,
          fromReturnId: doc.taxReturnId,
          toReturnId: assignSelectedId,
          fileName: doc.fileName,
        });
      } catch (err) {
        failures.push(`${doc.fileName}: ${err instanceof Error ? err.message : t('clientUploadsPage.errors.unknownError')}`);
      }
      setBulkAssignProgress({ current: i + 1, total: bulkAssignDocs.length });
    }
    setBulkAssigning(false);
    queryClient.invalidateQueries({ queryKey: ['portalUploads'] });
    // Record whatever succeeded for undo (even on partial failure)
    if (succeeded.length > 0) {
      const picked = allReturns.find(r => r.id === assignSelectedId);
      const targetName = picked ? `${picked.clientName} (${picked.taxYear})` : t('clientUploadsPage.undoBanner.fallbackTargetName');
      setLastAssignOp({ movedDocs: succeeded, targetName });
    }
    if (failures.length > 0) {
      setBulkAssignError(t('clientUploadsPage.dialogs.bulkAssign.failuresSummary', {
        failedCount: failures.length,
        total: bulkAssignDocs.length,
        details: failures.join('\n'),
      }));
      return;
    }
    // Clean close on full success
    setBulkAssignDocs(null);
    setBulkAssignProgress(null);
    setAssignSelectedId('');
    setAssignFilter('');
    setSelectedIds(new Set());
  };

  const closeBulkAssign = () => {
    if (bulkAssigning) return;
    setBulkAssignDocs(null);
    setBulkAssignProgress(null);
    setBulkAssignError(null);
    setAssignSelectedId('');
    setAssignFilter('');
  };

  const handleUndo = async () => {
    if (!lastAssignOp || undoing) return;
    setUndoing(true);
    const failures: string[] = [];
    for (const moved of lastAssignOp.movedDocs) {
      try {
        // Reverse: currently at toReturnId, move back to fromReturnId
        await reassignPortalUpload(moved.id, moved.toReturnId, moved.fromReturnId);
      } catch (err) {
        failures.push(`${moved.fileName}: ${err instanceof Error ? err.message : t('clientUploadsPage.errors.unknownError')}`);
      }
    }
    setUndoing(false);
    queryClient.invalidateQueries({ queryKey: ['portalUploads'] });
    if (failures.length > 0) {
      alert(t('clientUploadsPage.errors.undoPartialFailed', { details: failures.join('\n') }));
    }
    setLastAssignOp(null);
  };

  const returnOptions = useMemo(() => {
    const filter = assignFilter.toLowerCase().trim();
    const sorted = [...allReturns].sort((a, b) => (a.clientName || '').localeCompare(b.clientName || ''));
    if (!filter) return sorted.slice(0, 100);
    return sorted
      .filter(r =>
        r.clientName?.toLowerCase().includes(filter) ||
        r.taxYear?.toString().includes(filter)
      )
      .slice(0, 100);
  }, [allReturns, assignFilter]);

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>{t('clientUploadsPage.header.title')}</h2>
          <p>{t('clientUploadsPage.header.subtitle')}</p>
        </div>
        <div className={styles.filterBar}>
          <Input
            className={styles.searchInput}
            placeholder={t('clientUploadsPage.search.placeholder')}
            value={searchQuery}
            onChange={(_, data) => setSearchQuery(data.value)}
            contentBefore={<SearchRegular />}
          />
          <Checkbox
            checked={generalOnly}
            onChange={(_, data) => setGeneralOnly(!!data.checked)}
            label={t('clientUploadsPage.generalOnlyLabel')}
          />
          <Button
            appearance="primary"
            icon={<FolderZipRegular />}
            disabled={selectedIds.size === 0 || zipping}
            onClick={handleDownloadZip}
          >
            {zipping
              ? t('clientUploadsPage.downloadZip.preparing', { count: selectedIds.size })
              : selectedIds.size > 0
                ? t('clientUploadsPage.downloadZip.buttonWithCount', { count: selectedIds.size })
                : t('clientUploadsPage.downloadZip.button')}
          </Button>
          <Button
            appearance="subtle"
            icon={<FilterRegular />}
            onClick={() => refetch()}
          >
            {t('clientUploadsPage.refresh')}
          </Button>
        </div>
      </div>

      {lastAssignOp && (
        <div
          style={{
            marginBottom: '16px',
            padding: '12px 16px',
            backgroundColor: '#dff6dd',
            border: '1px solid #6bb700',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            flexWrap: 'wrap',
          }}
          role="status"
        >
          <span style={{ fontSize: tokens.fontSizeBase300 }}>
            {lastAssignOp.movedDocs.length === 1
              ? <Trans
                  i18nKey="clientUploadsPage.undoBanner.movedOne"
                  values={{ fileName: lastAssignOp.movedDocs[0].fileName, targetName: lastAssignOp.targetName }}
                  components={{ strong: <strong /> }}
                />
              : <Trans
                  i18nKey="clientUploadsPage.undoBanner.movedMany"
                  values={{ count: lastAssignOp.movedDocs.length, targetName: lastAssignOp.targetName }}
                  components={{ strong: <strong /> }}
                />
            }
          </span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              appearance="primary"
              size="small"
              disabled={undoing}
              onClick={handleUndo}
            >
              {undoing ? <Spinner size="tiny" /> : t('clientUploadsPage.undoBanner.undo')}
            </Button>
            <Button
              appearance="subtle"
              size="small"
              disabled={undoing}
              onClick={() => setLastAssignOp(null)}
            >
              {t('clientUploadsPage.undoBanner.dismiss')}
            </Button>
          </div>
        </div>
      )}

      {isLoading && (
        <div className="card" style={{ padding: '40px' }}>
          <TableSkeleton />
        </div>
      )}

      {error && (
        <div className="card" style={{ backgroundColor: '#fde7e9', color: '#a4262c' }}>
          {error instanceof Error ? error.message : t('clientUploadsPage.errors.loadFailed')}
        </div>
      )}

      {!isLoading && !error && filteredDocuments.length === 0 && (
        <div className="card">
          <div className="empty-state">
            <DocumentRegular style={{ fontSize: '48px', color: '#c8c8c8' }} />
            <p style={{ marginTop: '16px' }}>
              {searchQuery ? t('clientUploadsPage.emptyState.noSearchResults') : t('clientUploadsPage.emptyState.noUploads')}
            </p>
            <p style={{ fontSize: tokens.fontSizeBase300, color: 'var(--tl-text-secondary)' }}>
              {t('clientUploadsPage.emptyState.hint')}
            </p>
            {searchQuery && (
              <Button
                appearance="subtle"
                onClick={() => setSearchQuery('')}
                style={{ marginTop: '12px' }}
              >
                {t('clientUploadsPage.emptyState.clearSearch')}
              </Button>
            )}
          </div>
        </div>
      )}

      {!isLoading && !error && filteredDocuments.length > 0 && (
        <>
          {generalDocs.length > 0 && (
            <div className="card" style={{ marginBottom: '24px' }}>
              <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
                <div>
                  <h3 style={{ margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {t('clientUploadsPage.general.heading')}
                    <Badge appearance="filled" color="important">{generalDocs.length}</Badge>
                  </h3>
                  <p style={{ margin: 0, fontSize: tokens.fontSizeBase300, color: 'var(--tl-text-secondary)' }}>
                    {t('clientUploadsPage.general.description')}
                  </p>
                </div>
                <Button
                  appearance="primary"
                  icon={<LinkRegular />}
                  disabled={selectedGeneralDocs.length === 0}
                  onClick={() => setBulkAssignDocs(selectedGeneralDocs)}
                >
                  {selectedGeneralDocs.length > 0
                    ? t('clientUploadsPage.general.assignButtonWithCount', { count: selectedGeneralDocs.length })
                    : t('clientUploadsPage.general.assignButton')}
                </Button>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell style={{ width: '40px' }}>
                      <Checkbox
                        checked={generalDocs.length > 0 && generalDocs.every(d => selectedIds.has(d.id))}
                        onChange={() => toggleSelectAll(generalDocs)}
                      />
                    </TableHeaderCell>
                    <TableHeaderCell>{t('clientUploadsPage.common.file')}</TableHeaderCell>
                    <TableHeaderCell>{t('clientUploadsPage.general.uploadedByHeader')}</TableHeaderCell>
                    <TableHeaderCell>{t('clientUploadsPage.common.date')}</TableHeaderCell>
                    <TableHeaderCell>{t('clientUploadsPage.common.size')}</TableHeaderCell>
                    <TableHeaderCell>{t('clientUploadsPage.common.actions')}</TableHeaderCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {generalDocs.map(doc => (
                    <TableRow key={doc.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedIds.has(doc.id)}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSelected(doc.id, 'general', generalDocs, e.shiftKey);
                          }}
                          onChange={() => { /* handled in onClick to capture shiftKey */ }}
                        />
                      </TableCell>
                      <TableCell>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <DocumentRegular />
                          <div>
                            <div style={{ fontWeight: 500 }}>{doc.fileName}</div>
                            <div style={{ fontSize: tokens.fontSizeBase200, color: 'var(--tl-text-secondary)' }}>
                              {doc.fileType?.toUpperCase() || t('clientUploadsPage.common.fileTypeFallback')}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                          <span style={{ color: 'var(--tl-text-secondary)' }}>
                            {doc.uploadedByName || doc.emailFrom || t('clientUploadsPage.general.unknownUploader')}
                          </span>
                          {doc.sourceReturnId && (
                            <Tooltip content={t('clientUploadsPage.general.viaNotificationTooltip')} relationship="label">
                              <Badge appearance="tint" color="informative" size="small">{t('clientUploadsPage.general.viaNotificationBadge')}</Badge>
                            </Tooltip>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{formatDate(doc.uploadedAt)}</TableCell>
                      <TableCell>{formatFileSize(doc.fileSize)}</TableCell>
                      <TableCell>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          {doc.sourceReturnId && (() => {
                            // Spec §4.3 (D2c): one click to the return it was ACTUALLY uploaded
                            // for, no manual search. Guard: this must target doc.sourceReturnId
                            // exactly — the label names the target so a test can assert on it,
                            // not merely on "a button rendered" (the manual Assign button above
                            // already satisfies that weaker claim).
                            const target = returnsById.get(doc.sourceReturnId);
                            const label = target
                              ? t('clientUploadsPage.general.oneClickReassignWithTarget', { clientName: target.clientName, taxYear: target.taxYear })
                              : t('clientUploadsPage.general.oneClickReassignFallback');
                            return (
                              <Tooltip content={label} relationship="label">
                                <Button
                                  appearance="primary"
                                  icon={<LinkRegular />}
                                  size="small"
                                  disabled={reassignMutation.isPending}
                                  onClick={() => reassignMutation.mutate({ doc, newReturnId: doc.sourceReturnId! })}
                                >
                                  {label}
                                </Button>
                              </Tooltip>
                            );
                          })()}
                          <Tooltip content={t('clientUploadsPage.general.assignTooltip')} relationship="label">
                            <Button
                              appearance="subtle"
                              icon={<LinkRegular />}
                              size="small"
                              onClick={() => setAssignTarget(doc)}
                            >
                              {t('clientUploadsPage.general.manualAssignButton')}
                            </Button>
                          </Tooltip>
                          {doc.blobUrl && (
                            <>
                              <Tooltip content={t('clientUploadsPage.common.view')} relationship="label">
                                <Button
                                  as="a"
                                  appearance="subtle"
                                  icon={<OpenRegular />}
                                  size="small"
                                  href={doc.blobUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                />
                              </Tooltip>
                              <Tooltip content={t('clientUploadsPage.common.download')} relationship="label">
                                <Button
                                  appearance="subtle"
                                  icon={<ArrowDownloadRegular />}
                                  size="small"
                                  onClick={() => {
                                    const link = document.createElement('a');
                                    link.href = doc.blobUrl!;
                                    link.download = doc.fileName;
                                    link.click();
                                  }}
                                />
                              </Tooltip>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {!generalOnly && linkedDocs.length > 0 && (
            <div className="card">
              <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
                <div>
                  <h3 style={{ margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {t('clientUploadsPage.linked.heading')}
                    <Badge appearance="tint" color="informative">
                      {linkedFiltersActive ? `${linkedDocsFiltered.length}/${linkedDocs.length}` : linkedDocs.length}
                    </Badge>
                  </h3>
                  <p style={{ margin: 0, fontSize: tokens.fontSizeBase300, color: 'var(--tl-text-secondary)' }}>
                    {t('clientUploadsPage.linked.description')}
                  </p>
                </div>
                <Button
                  appearance="primary"
                  icon={<LinkRegular />}
                  disabled={selectedLinkedDocs.length === 0}
                  onClick={() => setBulkAssignDocs(selectedLinkedDocs)}
                >
                  {selectedLinkedDocs.length > 0
                    ? t('clientUploadsPage.linked.reassignButtonWithCount', { count: selectedLinkedDocs.length })
                    : t('clientUploadsPage.linked.reassignButton')}
                </Button>
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center', marginBottom: '16px' }}>
                <Combobox
                  placeholder={t('clientUploadsPage.linked.filterClientPlaceholder')}
                  value={linkedClientFilter}
                  selectedOptions={linkedClientFilter ? [linkedClientFilter] : []}
                  onOptionSelect={(_, data) => setLinkedClientFilter(data.optionValue || '')}
                  style={{ minWidth: '180px' }}
                  clearable
                >
                  {linkedClientOptions.map(name => (
                    <Option key={name} value={name}>{name}</Option>
                  ))}
                </Combobox>
                <Combobox
                  placeholder={t('clientUploadsPage.linked.filterYearPlaceholder')}
                  value={linkedYearFilter}
                  selectedOptions={linkedYearFilter ? [linkedYearFilter] : []}
                  onOptionSelect={(_, data) => setLinkedYearFilter(data.optionValue || '')}
                  style={{ minWidth: '120px' }}
                  clearable
                >
                  {linkedYearOptions.map(y => (
                    <Option key={y} value={String(y)}>{String(y)}</Option>
                  ))}
                </Combobox>
                <Combobox
                  placeholder={t('clientUploadsPage.linked.filterDatePlaceholder')}
                  value={dateRangeLabel(linkedDateRangeFilter)}
                  selectedOptions={[linkedDateRangeFilter]}
                  onOptionSelect={(_, data) => setLinkedDateRangeFilter((data.optionValue as typeof linkedDateRangeFilter) || 'all')}
                  style={{ minWidth: '160px' }}
                >
                  <Option value="all">{t('clientUploadsPage.linked.dateRange.all')}</Option>
                  <Option value="7d">{t('clientUploadsPage.linked.dateRange.last7')}</Option>
                  <Option value="30d">{t('clientUploadsPage.linked.dateRange.last30')}</Option>
                  <Option value="90d">{t('clientUploadsPage.linked.dateRange.last90')}</Option>
                  <Option value="thisYear">{t('clientUploadsPage.linked.dateRange.thisYear')}</Option>
                </Combobox>
                <Combobox
                  placeholder={t('clientUploadsPage.linked.filterFileTypePlaceholder')}
                  value={linkedFileTypeFilter}
                  selectedOptions={linkedFileTypeFilter ? [linkedFileTypeFilter] : []}
                  onOptionSelect={(_, data) => setLinkedFileTypeFilter(data.optionValue || '')}
                  style={{ minWidth: '140px' }}
                  clearable
                >
                  {linkedFileTypeOptions.map(ft => (
                    <Option key={ft} value={ft}>{ft}</Option>
                  ))}
                </Combobox>
                {linkedFiltersActive && (
                  <Button appearance="subtle" onClick={clearLinkedFilters}>
                    {t('clientUploadsPage.linked.clearFilters')}
                  </Button>
                )}
              </div>

              {linkedDocsFiltered.length === 0 ? (
                <div style={{ padding: '32px', textAlign: 'center', color: 'var(--tl-text-secondary)' }}>
                  <p style={{ margin: '0 0 12px 0' }}>{t('clientUploadsPage.linked.noFilterResults')}</p>
                  <Button appearance="secondary" onClick={clearLinkedFilters}>{t('clientUploadsPage.linked.clearFilters')}</Button>
                </div>
              ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell style={{ width: '40px' }}>
                      <Checkbox
                        checked={linkedDocsFiltered.length > 0 && linkedDocsFiltered.every(d => selectedIds.has(d.id))}
                        onChange={() => toggleSelectAll(linkedDocsFiltered)}
                      />
                    </TableHeaderCell>
                    <TableHeaderCell>{t('clientUploadsPage.common.file')}</TableHeaderCell>
                    <TableHeaderCell>{t('clientUploadsPage.linked.taxClientReturnHeader')}</TableHeaderCell>
                    <TableHeaderCell>{t('clientUploadsPage.common.date')}</TableHeaderCell>
                    <TableHeaderCell>{t('clientUploadsPage.common.size')}</TableHeaderCell>
                    <TableHeaderCell>{t('clientUploadsPage.common.actions')}</TableHeaderCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {linkedDocsFiltered.map(doc => (
                    <TableRow key={doc.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedIds.has(doc.id)}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSelected(doc.id, 'linked', linkedDocsFiltered, e.shiftKey);
                          }}
                          onChange={() => { /* handled in onClick to capture shiftKey */ }}
                        />
                      </TableCell>
                      <TableCell>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <DocumentRegular />
                          <div>
                            <div style={{ fontWeight: 500 }}>{doc.fileName}</div>
                            <div style={{ fontSize: tokens.fontSizeBase200, color: 'var(--tl-text-secondary)' }}>
                              {doc.fileType?.toUpperCase() || t('clientUploadsPage.common.fileTypeFallback')}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className={styles.clientLink}>
                          <FluentLink
                            {...realLinkProps(`/tax-returns/${doc.taxReturnId}`, { navigate })}
                          >
                            {doc.clientName || t('clientUploadsPage.linked.unknownClient')}
                          </FluentLink>
                          {doc.taxYear && (
                            <span style={{ fontSize: tokens.fontSizeBase200, color: 'var(--tl-text-secondary)' }}>
                              {t('clientUploadsPage.linked.taxYearLabel', { taxYear: doc.taxYear })}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{formatDate(doc.uploadedAt)}</TableCell>
                      <TableCell>{formatFileSize(doc.fileSize)}</TableCell>
                      <TableCell>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <Tooltip content={t('clientUploadsPage.linked.reassignTooltip')} relationship="label">
                            <Button
                              appearance="subtle"
                              icon={<LinkRegular />}
                              size="small"
                              onClick={() => setAssignTarget(doc)}
                            >
                              {t('clientUploadsPage.linked.reassignButtonLabel')}
                            </Button>
                          </Tooltip>
                          {doc.blobUrl && (
                            <>
                              <Tooltip content={t('clientUploadsPage.common.view')} relationship="label">
                                <Button
                                  as="a"
                                  appearance="subtle"
                                  icon={<OpenRegular />}
                                  size="small"
                                  href={doc.blobUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                />
                              </Tooltip>
                              <Tooltip content={t('clientUploadsPage.common.download')} relationship="label">
                                <Button
                                  appearance="subtle"
                                  icon={<ArrowDownloadRegular />}
                                  size="small"
                                  onClick={() => {
                                    const link = document.createElement('a');
                                    link.href = doc.blobUrl!;
                                    link.download = doc.fileName;
                                    link.click();
                                  }}
                                />
                              </Tooltip>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              )}
            </div>
          )}

          <div style={{ marginTop: '16px', padding: '12px', backgroundColor: 'var(--tl-bg-subtle)', borderRadius: '4px', fontSize: tokens.fontSizeBase300, color: 'var(--tl-text-secondary)' }}>
            {searchQuery
              ? t('clientUploadsPage.footer.showing', { filtered: filteredDocuments.length, total: documents.length })
              : t('clientUploadsPage.footer.total', { total: documents.length, general: generalDocs.length, linked: linkedDocs.length })}
          </div>
        </>
      )}

      <Dialog
        open={assignTarget !== null}
        onOpenChange={(_, data) => {
          if (!data.open) {
            setAssignTarget(null);
            setAssignSelectedId('');
            setAssignFilter('');
          }
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>
              {assignTarget?.taxReturnId === 'general'
                ? t('clientUploadsPage.dialogs.assign.titleAssign')
                : t('clientUploadsPage.dialogs.assign.titleReassign')}
            </DialogTitle>
            <DialogContent>
              <p style={{ marginTop: 0 }}>
                <Trans
                  i18nKey="clientUploadsPage.dialogs.assign.body"
                  values={{ fileName: assignTarget?.fileName }}
                  components={{ strong: <strong /> }}
                />
              </p>
              <Combobox
                placeholder={t('clientUploadsPage.dialogs.comboPlaceholder')}
                value={assignFilter}
                onInput={(e) => setAssignFilter((e.target as HTMLInputElement).value)}
                onOptionSelect={(_, data) => {
                  setAssignSelectedId(data.optionValue || '');
                  const picked = allReturns.find(r => r.id === data.optionValue);
                  if (picked) setAssignFilter(`${picked.clientName} (${picked.taxYear})`);
                }}
                style={{ width: '100%' }}
              >
                {returnOptions.map(r => (
                  <Option key={r.id} value={r.id} text={`${r.clientName} (${r.taxYear})`}>
                    {t('clientUploadsPage.dialogs.optionLabel', { clientName: r.clientName, taxYear: r.taxYear })}
                  </Option>
                ))}
              </Combobox>
              {reassignMutation.isError && (
                <p style={{ color: '#a4262c', marginTop: '8px' }}>
                  {t('clientUploadsPage.dialogs.assign.failedToReassign', {
                    message: reassignMutation.error instanceof Error ? reassignMutation.error.message : t('clientUploadsPage.errors.unknownError'),
                  })}
                </p>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => {
                  setAssignTarget(null);
                  setAssignSelectedId('');
                  setAssignFilter('');
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                appearance="primary"
                disabled={!assignSelectedId || reassignMutation.isPending}
                onClick={() => {
                  if (assignTarget && assignSelectedId) {
                    reassignMutation.mutate({ doc: assignTarget, newReturnId: assignSelectedId });
                  }
                }}
              >
                {reassignMutation.isPending ? <Spinner size="tiny" /> : t('clientUploadsPage.dialogs.assign.confirmButton')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <Dialog
        open={bulkAssignDocs !== null}
        onOpenChange={(_, data) => {
          if (!data.open) closeBulkAssign();
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>
              {bulkAssignDocs?.[0]?.taxReturnId === 'general'
                ? t('clientUploadsPage.dialogs.bulkAssign.titleAssign', { count: bulkAssignDocs?.length ?? 0 })
                : t('clientUploadsPage.dialogs.bulkAssign.titleReassign', { count: bulkAssignDocs?.length ?? 0 })}
            </DialogTitle>
            <DialogContent>
              <p style={{ marginTop: 0 }}>
                {t('clientUploadsPage.dialogs.bulkAssign.body', { count: bulkAssignDocs?.length ?? 0 })}
              </p>
              <Combobox
                placeholder={t('clientUploadsPage.dialogs.comboPlaceholder')}
                value={assignFilter}
                onInput={(e) => setAssignFilter((e.target as HTMLInputElement).value)}
                onOptionSelect={(_, data) => {
                  setAssignSelectedId(data.optionValue || '');
                  const picked = allReturns.find(r => r.id === data.optionValue);
                  if (picked) setAssignFilter(`${picked.clientName} (${picked.taxYear})`);
                }}
                style={{ width: '100%' }}
                disabled={bulkAssigning}
              >
                {returnOptions.map(r => (
                  <Option key={r.id} value={r.id} text={`${r.clientName} (${r.taxYear})`}>
                    {t('clientUploadsPage.dialogs.optionLabel', { clientName: r.clientName, taxYear: r.taxYear })}
                  </Option>
                ))}
              </Combobox>
              {bulkAssignProgress && (
                <p style={{ marginTop: '12px', fontSize: tokens.fontSizeBase300, color: 'var(--tl-text-secondary)' }}>
                  {t('clientUploadsPage.dialogs.bulkAssign.progress', { current: bulkAssignProgress.current, total: bulkAssignProgress.total })}
                </p>
              )}
              {bulkAssignError && (
                <pre style={{ color: '#a4262c', marginTop: '8px', fontSize: '13px', whiteSpace: 'pre-wrap' }}>
                  {bulkAssignError}
                </pre>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={closeBulkAssign}
                disabled={bulkAssigning}
              >
                {bulkAssignError ? t('common.close') : t('common.cancel')}
              </Button>
              <Button
                appearance="primary"
                disabled={!assignSelectedId || bulkAssigning || (bulkAssignDocs?.length ?? 0) === 0}
                onClick={handleBulkAssign}
              >
                {bulkAssigning
                  ? <Spinner size="tiny" />
                  : bulkAssignDocs?.[0]?.taxReturnId === 'general'
                    ? t('clientUploadsPage.dialogs.bulkAssign.assignButtonLabel', { count: bulkAssignDocs?.length ?? 0 })
                    : t('clientUploadsPage.dialogs.bulkAssign.reassignButtonLabel', { count: bulkAssignDocs?.length ?? 0 })}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
